import classNames from "classnames";
import * as _ from "lodash";
import React from "react";
import { ContentTypeEnumHelper, MonthEnumHelper } from "@travellocal/utils/enumHelper";
import { neverHere } from "@travellocal/utils/tsHelpers";
import { contentDetail } from "@travellocal/utils/webRoutes"
import { ItineraryCard } from "./ItineraryCard/ItineraryCard";
import { ReviewCard } from "./ReviewCard/ReviewCard";
import { BlogCard } from "./BlogCard/BlogCard";
import { LabelIcon, Icomoon } from "../..";
import "./ContentCard.scss";

interface ContentCardProps {
  /**
   * The piece of content to display on this card.
   */
  content: IContent | null;

  /**
   * Tags that are "selected" in some way in this card's surroundings. Matching ones will be highlighted.
   */
  filterContext?: IListContentParams;

  /**
   * Optional classname for the root element of the card.
   */
  className?: string;

  /**
   * Whether to show the "hero" variant of the card (N.B. Blog posts and reviews only)
   */
  isHero?: boolean;

  /**
   * Number alters focus state of links within content cards, and if/where it participates in sequential keyboard navigation.
   */
  tabIndex?: number;

  onClick?: () => void;
}

/**
 * Component to display a single piece of content from the /content API.
 * 
 * Layout notes
 * - The "non hero" version of cards should be placed so that they are between 200px and 450px in width. 
 * - Cards will expand to the size of their container, so either set the width explicitly or use e.g. Bulma columns to set the widths.
 * - Typically you should prefer to use the `CardContainer` component to display multiple cards; this component handles responsive layout, currency switching, and other logic.
 * 
 * @status stable
 */
export class ContentCard extends React.Component<ContentCardProps> {
  private delayClassIndex: number;

  public static defaultProps = {
    isHero: false,
  }

  public constructor(props: ContentCardProps) {
    super(props);
    this.delayClassIndex = Math.floor(Math.random() * 10) + 1;
  }

  private renderTags = (content: IContent): JSX.Element => {
    const summariseList = (list: { id: number; name: string }[], activeTags?: number[]) => {
      // Highlight and promote the tag matches.
      let primo: string[]; // Active tags which are present on the content card - these will be highlighted
      let norm: string[]; // Other tags present on the content card

      if (activeTags != null && activeTags.length > 0) {
        const lists = _.groupBy(list, (x) => _.some(activeTags, (id) => x.id === id));
        primo = lists["true"] ? lists["true"].map(x => x.name) : [];
        norm = lists["false"] ? lists["false"].map(x => x.name) : [];
      }
      else {
        primo = [];
        norm = list.map(x => x.name);
      }

      const max = 3;

      const tags: (JSX.Element | string)[] = [];
      while (primo.length > 0 && tags.length < max) {
        tags.push(<span key={tags.length}><em>{primo.splice(0, 1)[0]}</em></span>);
      }
      while (norm.length > 0 && tags.length < max) {
        tags.push(<span key={tags.length}>{norm.splice(0, 1)[0]}</span>);
      }
      // Commas between tags are inserted by CSS

      const remaining = primo.length + norm.length;
      if (remaining > 0) {
        tags.push(`, +${remaining}`);
      }
      return tags;
    };

    const countries = content.locations?.filter(x => x.locationType === LocationType.Country) ?? [];
    const filterContext = this.props.filterContext ?? {};

    const author = content.author ? content.author.firstName : content.guestAuthor;

    return (
      <div className="card-tags">
        {content.type === ContentType.Review &&
          <LabelIcon
            icomoon={Icomoon.user}
            iconColor="accent"
            labelText={`${author} from ${content.content.customerLocation}`}
            labelTextColor={this.props.isHero ? "light" : "mid"}
            containerClassNames={["author", "card-tag"]} />
        }
        {countries.length > 0 &&
          <LabelIcon
            icomoon={Icomoon.location}
            iconColor="accent"
            labelText={summariseList(countries, filterContext.locations)}
            labelTextColor={this.props.isHero && content.type !== ContentType.BlogPost ? "light" : "mid"}
            containerClassNames={["locations", "card-tag"]} />
        }
        {content.months?.length > 0 &&
          <LabelIcon
            icomoon={Icomoon.calendar}
            iconColor="accent"
            labelText={summariseList(content.months.map(month => ({
              id: month,
              name: MonthEnumHelper.toFriendlyName(month)
            })), filterContext.months)}
            labelTextColor={this.props.isHero && content.type !== ContentType.BlogPost ? "light" : "mid"}
            containerClassNames={["months", "card-tag"]} />
        }
        {content.themes?.length > 0 &&
          <LabelIcon
            icomoon={Icomoon.tag}
            iconColor="accent"
            labelText={summariseList(content.themes, filterContext.themes)}
            labelTextColor={this.props.isHero && content.type !== ContentType.BlogPost ? "light" : "mid"}
            containerClassNames={["themes", "card-tag"]} />
        }
      </div>
    );
  }

  public render() {
    const { content, className, isHero, tabIndex, onClick } = this.props;
    if (!content) {
      return null;
    }

    const commonProps = {
      content,
      className: classNames(
        "content-card",
        className,
        `${ContentTypeEnumHelper.toClassName(content.type)}`,
      ),
      // eslint-disable-next-line no-nested-ternary
      heroImageUrl: !content.heroImage
        ? "/images/missing-image.jpg"
        : (isHero ? content.heroImage.largeUrl : content.heroImage.smallUrl),
      renderTags: this.renderTags,
      delayClassIndex: this.delayClassIndex,
      tabIndex,
      onClick,
      heroImageMeta: content?.heroImage?.meta || "",
      url: contentDetail(content),
      isHero: isHero ?? false,
    };

    switch (content.type) {
      case ContentType.SampleItinerary:
        return (
          <ItineraryCard
            {...commonProps}
            content={content}
          />
        );
      case ContentType.Review:
        return (
          <ReviewCard
            {...commonProps}
            content={content}
          />
        );
      case ContentType.LocationSection:
      case ContentType.BlogPost:
        return (
          <BlogCard
            {...commonProps}
            content={content}
          />
        );
      default:
        return neverHere(content, "content card");
    }
  }
}
